import {
  Icon,
  PeriodSelect,
  ResponsiveFlex,
  ResponsiveSize,
  Shadow,
  Text,
  Skeleton,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Box } from 'native-base';
import React, { useMemo } from 'react';

export declare namespace BigNumberCardContainerTypes {
  interface PeriodOption {
    label: string;
    value: string;
  }

  interface Props {
    isLoading?: boolean;
    /**
     * NOTE: Required if `isLoading` is false.
     */
    periodOptions?: PeriodOption | PeriodOption[];
    /**
     * NOTE: Required if `isLoading` is false.\
     * The value of the selected option.
     */
    periodText?: string;
    selectedPeriod?: string;
    onPeriodChange?: (value: string) => void;
    children: React.ReactNode;
    width?: ResponsiveSize;
    minWidth?: ResponsiveSize;
    flex?: ResponsiveFlex;
  }
}

export const BigNumberCardContainer: React.FC<BigNumberCardContainerTypes.Props> = ({
  isLoading,
  selectedPeriod,
  children,
  periodOptions,
  periodText,
  ...props
}) => {
  const normalizedPeriodOptions = periodOptions
    ? Array.isArray(periodOptions)
      ? periodOptions
      : [periodOptions]
    : [];

  if (!isLoading && (!periodOptions || !selectedPeriod)) {
    throw new Error('periodOptions and selectedPeriod are required when isLoading is false');
  }

  const periodOptionsContent = useMemo(() => {
    if (isLoading) {
      return (
        <HStack justifyContent="space-between">
          <HStack width="1/6" space={Spacing['stack.nano']}>
            <Skeleton height="16px" borderRadius="full" />
            <Skeleton height="16px" borderRadius="full" />
          </HStack>
          <Skeleton height="16px" borderRadius="full" width="2/6" />
        </HStack>
      );
    }

    const isSinglePeriod = normalizedPeriodOptions.length === 1;
    let periodSelectContent: JSX.Element | null = null;

    if (isSinglePeriod) {
      periodSelectContent = (
        <HStack space={Spacing['stack.nano']} alignItems="center" justifyContent="flex-start">
          <Icon name="Calendar" size="xxs" testId="period-icon" color={Color['neutral.medium']} />
          <Text color="neutral.medium">Período</Text>
          <Text variant="semibold">
            {normalizedPeriodOptions.find((period) => period.value === selectedPeriod)!.label}
          </Text>
        </HStack>
      );
    } else {
      periodSelectContent = (
        <PeriodSelect
          label="Período"
          periodOptions={normalizedPeriodOptions}
          selectedPeriod={selectedPeriod || ''}
          padding={0}
        />
      );
    }

    return (
      <HStack space={Spacing['stack.nano']} alignItems="center" justifyContent="space-between">
        {periodSelectContent}
        <Text color="neutral.medium">{periodText}</Text>
      </HStack>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodOptions, selectedPeriod, isLoading]);

  return (
    <Box {...props}>
      <Shadow flex={1} level={1} testId="shadow" borderRadius={BoxRadius.md}>
        <VStack space={Spacing['stack.nano']} padding={Spacing['stack.xxxs']} overflow="hidden">
          {periodOptionsContent}
          <HStack space={Spacing['stack.xxxs']}>{children}</HStack>
        </VStack>
      </Shadow>
    </Box>
  );
};
